import { useQuery } from '@tanstack/react-query'
import { formatMoney } from '@johnsaks/core'
import { EmptyState } from '@/components/empty-state'
import { Kpi } from '@/components/kpi'
import { StatusPill } from '@/components/status-pill'
import { api } from '@/lib/api'
import { fmtDateTime, ORDER_STATUS } from '@/lib/labels'

type DashboardPayload = {
  kpis: {
    ordersToday: number
    revenueCents: number
    orderedCents?: number
    paidCents?: number
    pendingOrders: number
    toPrepare?: number
    codUnpaid?: number
    lowStock: number
    todo?: number
  }
  todo?: {
    pending: number
    toPrepare: number
    codUnpaid: number
  }
  salesSeries: Array<{ date: string; cents: number; paidCents?: number }>
  paymentMix: Array<{ method: string; cents: number }>
  stockAlerts: Array<{
    id: string
    sku: string
    name: string
    available: number
    threshold: number
    tone: string
  }>
  recentOrders: Array<{
    reference: string
    status: string
    totalCents: number
    contactName: string
    placedAt: string
  }>
}

const STOCK_TONE: Record<string, string> = {
  in: 'ok',
  low: 'warn',
  out: 'bad',
}

export function Dashboard() {
  const dash = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => api<DashboardPayload>('/admin/dashboard'),
  })
  const livraison = useQuery({
    queryKey: ['admin-delivery-settings'],
    queryFn: () =>
      api<{ homeFeeCents: number; pickupFeeCents: number; freeAboveCents: number }>(
        '/admin/delivery/settings',
      ),
  })

  if (dash.isError) {
    return (
      <div className="carte-admin p-6">
        <p className="text-rouge-texte">
          L’API ne répond pas. Vérifiez qu’elle tourne sur le port 4000, puis rechargez.
        </p>
      </div>
    )
  }

  const k = dash.data?.kpis
  const todo = dash.data?.todo
  const series = dash.data?.salesSeries ?? []
  const maxCents = Math.max(1, ...series.map((s) => s.cents))
  const alerts = dash.data?.stockAlerts ?? []
  const recent = dash.data?.recentOrders ?? []
  const orderedCents = k?.orderedCents ?? k?.revenueCents ?? 0
  const paidCents = k?.paidCents ?? 0

  return (
    <div className="flex flex-col gap-5">
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Commandes du jour"
          valeur={dash.isLoading ? '—' : String(k?.ordersToday ?? 0)}
        />
        <Kpi
          label="CA commandé"
          valeur={dash.isLoading ? '—' : formatMoney(orderedCents)}
          detail="totalCents du jour (hors annulées)"
        />
        <Kpi
          label="CA encaissé"
          valeur={dash.isLoading ? '—' : formatMoney(paidCents)}
          detail="paidCents du jour"
        />
        <Kpi
          label="Alertes stock"
          valeur={dash.isLoading ? '—' : String(k?.lowStock ?? 0)}
          detail={k?.lowStock ? 'niveaux variante' : 'rien à signaler'}
        />
      </section>

      <section className="carte-admin">
        <div className="flex items-center justify-between border-b border-bordure-admin px-4 py-3">
          <h2 className="text-base">À faire</h2>
          <span className="kicker text-second-admin">
            {dash.isLoading ? '…' : `${k?.todo ?? 0} actions`}
          </span>
        </div>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Kpi label="En attente" valeur={String(todo?.pending ?? k?.pendingOrders ?? 0)} />
          <Kpi label="À préparer" valeur={String(todo?.toPrepare ?? k?.toPrepare ?? 0)} />
          <Kpi label="COD non payés" valeur={String(todo?.codUnpaid ?? k?.codUnpaid ?? 0)} />
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <section className="carte-admin">
          <div className="flex items-center justify-between border-b border-bordure-admin px-4 py-3">
            <h2 className="text-base">Ventes · 14 jours</h2>
            <span className="kicker text-second-admin">CA journalier</span>
          </div>
          {dash.isLoading ? (
            <p className="p-4 text-second-admin">Chargement…</p>
          ) : series.length === 0 ? (
            <EmptyState title="Aucune vente" body="Les barres apparaîtront dès les premières commandes." />
          ) : (
            <div className="flex h-48 items-end gap-1.5 px-4 py-4">
              {series.map((s) => {
                const h = Math.max(4, Math.round((s.cents / maxCents) * 100))
                return (
                  <div key={s.date} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                    <div
                      className="w-full rounded-t-sm bg-encre-admin/80"
                      style={{ height: `${h}%` }}
                      title={`${s.date}: commandé ${formatMoney(s.cents)}${
                        s.paidCents != null ? ` · encaissé ${formatMoney(s.paidCents)}` : ''
                      }`}
                    />
                    <span className="kicker truncate text-[9px] text-second-admin">
                      {s.date.slice(8)}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </section>

        <section className="carte-admin">
          <div className="flex items-center justify-between border-b border-bordure-admin px-4 py-3">
            <h2 className="text-base">Alertes de stock</h2>
            <a href="/stocks" className="kicker text-rouge">
              Voir stocks
            </a>
          </div>
          {alerts.length === 0 && !dash.isLoading ? (
            <p className="p-4 text-sm text-second-admin">Aucune alerte pour le moment.</p>
          ) : (
            <ul className="flex flex-col">
              {alerts.slice(0, 8).map((a) => (
                <li
                  key={`${a.id}-${a.sku}`}
                  className="flex items-center justify-between gap-3 border-b border-bordure-admin px-4 py-2.5 last:border-0"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-encre-admin">{a.name}</p>
                    <p className="text-xs text-second-admin">
                      {a.sku} · {a.available} / seuil {a.threshold}
                    </p>
                  </div>
                  <StatusPill
                    label={a.tone === 'out' ? 'Épuisé' : 'Stock bas'}
                    tone={STOCK_TONE[a.tone] ?? 'warn'}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="carte-admin">
        <div className="flex items-center justify-between border-b border-bordure-admin px-4 py-3">
          <h2 className="text-base">Commandes récentes</h2>
          <a href="/commandes" className="kicker text-rouge">
            Toutes les commandes
          </a>
        </div>
        {recent.length === 0 && !dash.isLoading ? (
          <EmptyState
            title="Pas encore de commandes"
            body="Les commandes passées sur la boutique apparaîtront ici."
            action={
              <a href="/commandes" className="kicker text-rouge">
                Ouvrir les commandes
              </a>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-bordure-admin">
                  {['Réf.', 'Client', 'Statut', 'Total', 'Date'].map((h) => (
                    <th key={h} className="px-4 py-2.5">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.map((o) => {
                  const st = ORDER_STATUS[o.status] ?? { label: o.status, tone: 'neutral' }
                  return (
                    <tr key={o.reference} className="border-b border-bordure-admin last:border-0">
                      <td className="px-4 py-2.5">
                        <a
                          href={`/commandes/${o.reference}`}
                          className="font-medium text-encre-admin underline-offset-2 hover:underline"
                        >
                          {o.reference}
                        </a>
                      </td>
                      <td className="px-4 py-2.5">{o.contactName}</td>
                      <td className="px-4 py-2.5">
                        <StatusPill label={st.label} tone={st.tone} />
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">{formatMoney(o.totalCents)}</td>
                      <td className="px-4 py-2.5 text-second-admin">{fmtDateTime(o.placedAt)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="carte-admin p-4">
        <h2 className="text-base">Frais de livraison</h2>
        <p className="mt-1 text-xs text-second-admin">Source unique, lue par la boutique.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Kpi
            label="Livraison à domicile"
            valeur={formatMoney(livraison.data?.homeFeeCents ?? 0)}
          />
          <Kpi
            label="Retrait en point relais"
            valeur={formatMoney(livraison.data?.pickupFeeCents ?? 0)}
          />
          <Kpi
            label="Offerte à partir de"
            valeur={formatMoney(livraison.data?.freeAboveCents ?? 0)}
          />
        </div>
      </section>
    </div>
  )
}
